import React, { lazy, Suspense } from 'react'
import { claims } from '../claims'
import { About } from '../components/content/About'
import { CompanyModule } from '../components/content/account/company/CompanyModule'
import { AddCompanyEntry } from '../components/content/account/company/entry/AddCompanyEntry'
import { UpdateCompanyEntry } from '../components/content/account/company/entry/UpdateCompanyEntry'
import { Profile } from '../components/content/account/profile/Profile'
import { Dashboard } from '../components/content/dashboard/Dashboard'
import { EducationReportEntry } from '../components/content/document/education-report/entry/EducationReportEntry'
import { EducationReportList } from '../components/content/document/education-report/list/EducationReportList'
import { TrialPeriodReportEdit } from '../components/content/document/trial-period-report/entry/TrialPeriodReportEdit'

import { CreateApprenticeGradeContainers } from '../components/content/grade-module/grade-overview/CreateApprenticeGradeContainers'

import { AddEducationPlan } from '../components/content/grade-module/module/education-plan/AddEducationPlan'
import { UpdateEducationPlan } from '../components/content/grade-module/module/education-plan/UpdateEducationPlan'
import { ApprenticeshipModuleModule } from '../components/content/grade-module/module/modules/ApprenticeshipModule.module'
import { AccountList } from '../components/content/hrm/account/AccountList'
import { EditAccount } from '../components/content/hrm/account/EditAccount'
import { EditPerson } from '../components/content/hrm/entry/EditPerson'
import { GroupEntry } from '../components/content/hrm/group/GroupEntry'
import { GroupModule } from '../components/content/hrm/group/GroupModule'

import { LearningJournal } from '../components/content/journal/Journal.module'
import { JournalDetail } from '../components/content/journal/JournalDetail'
import { JournalEntry } from '../components/content/journal/JournalEntry'
import { LoadingSpinner } from '../components/util/loading-spinner/LoadingSpinner'

const HrmModule = lazy(() => import('../components/content/hrm/HrmModule'))
const ObservationModule = lazy(
  () => import('../components/content/observation/ObservationModule')
)

const GradeModule = lazy(
  () => import('../components/content/grade-module/grade-entry/Grade.module')
)

const EducationPlanModule = lazy(
  () =>
    import(
      '../components/content/grade-module/module/education-plan/EducationPlan.module'
    )
)

const GradeManagementModule = lazy(
  () => import('../components/content/grade-module/GradeManagement.module')
)

const AllDossiersList = lazy(
  () => import('../components/content/dossier/AllDossiersList')
)

const TrialPeriodReportList = lazy(
  () =>
    import(
      '../components/content/document/trial-period-report/list/TrialPeriodReportList'
    )
)

// Main- and Sub-Module Description:
// https://xenia-software.atlassian.net/wiki/spaces/MOKAI/pages/6455341/1.0.0+API+Access+Management

export interface MokaiRoute {
  path: string;
  exact: boolean;
  name: string;
  icon: string;
  component: JSX.Element;
  mainModule: string;
  subModules?: string[];
}

// Routes visible to signed-in users
export enum AuthRoutes {
  dashboard = '/home',
  hrm = '/hrm',
  editPerson = '/hrm/:id/:tab',
  addPerson = '/hrm/+',
  dossiers = '/hrm/dossiers',
  groups = '/hrm/groups',
  editGroup = 'hrm/groups/:id',
  accounts = '/hrm/accounts',
  editAccount = '/hrm/accounts/:id',
  educationReports = '/educationReports',
  educationReportApprentice = '/educationReports/:id',
  educationReportEntry = '/educationReports/:id/:semester',
  observation = '/observation',
  trialPeriodReport = '/trialPeriodReports',
  trialPeriodReportEntry = '/trialPeriodReports/:id',
  trialPeriodReportNew = '/trialPeriodReports/+',
  profile = '/profile',
  settings = '/settings',
  company = '/company',
  companyEntry = '/company/:id',
  addCompany = '/company/+',
  modules = '/modules',
  educationPlans = '/educationPlans',
  addEducationPlan = '/educationPlans/+',
  editEducationPlan = '/educationPlans/:id',
  grades = '/grades',
  editGrade = '/grades/:id',
  assignGradeContainer = '/grade_assignment',
  assignGradeContainerById = '/grade_assignment/:id',
  journals = '/learning_journals',
  addJournal = '/learning_journals/+',
  editJournal = '/learning_journals/:id',
  listJournals = '/learning_journals/list'
}

// Routes visible to all
export enum PublicRoutes {
  login = '/login',
  register = '/register', // todo
  contact = '/contact',
  notFound = '*',
  about = '/about',
}

export const SidebarRoutes_Authorized: MokaiRoute[] = [
  {
    path: AuthRoutes.dashboard,
    exact: true,
    name: 'dashboard',
    icon: 'fas fa-home fa-lg',
    component: <Dashboard />,
    mainModule: claims.basic.main,
  },
  {
    path: AuthRoutes.hrm,
    exact: true,
    name: 'human_resources',
    icon: 'fas fa-users fa-lg',
    component: (
      <React.Suspense fallback={<LoadingSpinner />}>
        <HrmModule />
      </React.Suspense>
    ),
    mainModule: claims.hrm.main,
    subModules: [
      claims.hrm.persons.main, // '001-001-000',
      claims.hrm.person.main, // '001-002-000',
      claims.hrm.personAccount.main, //'001-003-000',
      claims.hrm.account.main, // '001-004-000',
      claims.hrm.apprentices.main, //'001-005-000',
      claims.hrm.employees.main, //'001-006-000',
      claims.hrm.groups.main, //'001-007-000',
      claims.hrm.professions.main, //'001-008-000'
    ],
  },
  {
    path: AuthRoutes.observation,
    exact: true,
    name: 'observation',
    icon: 'fas fa-star-half-alt fa-lg',
    component: (
      <React.Suspense fallback={<LoadingSpinner />}>
        <ObservationModule />
      </React.Suspense>
    ),
    mainModule: claims.documents.main, //'003-000-000',
    subModules: [
      claims.documents.observations.main, //'003-003-000'
    ],
  },
  {
    path: AuthRoutes.journals,
    exact: true,
    component: <LearningJournal/>,
    name: 'journals',
    icon: 'fas fa-solid fa-lg fa-book',
    mainModule: claims.learningJournals.main,
    subModules: [
      claims.learningJournals.general.get,
      claims.learningJournals.general.getAll
    ]
  },
  {
    path: AuthRoutes.grades,
    exact: true,
    name: 'grade_management',
    icon: 'fa-solid fa-n fa-lg',

    component: (
      <Suspense fallback={<LoadingSpinner />}>
        <GradeManagementModule />
      </Suspense>
    ),
    mainModule: '', // claims.gradeModule.main
    subModules: [
      claims.gradeModule.apprenticeshipModuleGrades.main, // 004-005-000
      claims.gradeModule.apprenticeshipModuleGrades.getAll, //004-005-001
      claims.gradeModule.apprenticeshipModuleGrades.create, // 004-005-002
      claims.gradeModule.apprenticeshipModuleGrades.getSingle, // 004-005-003
      claims.gradeModule.apprenticeshipModuleGrades.edit, // 004-005-004
      claims.gradeModule.apprenticeshipModuleGrades.delete, // 004-005-005
    ],
  },
  {
    path: AuthRoutes.educationReports,
    exact: true,
    name: 'education_reports',
    icon: 'fas fa-graduation-cap fa-lg',
    component: <EducationReportList />,
    mainModule: claims.documents.main,
    subModules: [claims.documents.educationReports.main],
  },
  {
    path: AuthRoutes.trialPeriodReport,
    exact: true,
    name: 'trial_period_reports',
    icon: 'fas fa-clipboard-check fa-lg',
    component: (
      <Suspense fallback={<LoadingSpinner />}>
        <TrialPeriodReportList />
      </Suspense>
    ),

    mainModule: claims.documents.main,
    subModules: [
      claims.documents.trialPeriodReport.main,
      claims.documents.trialPeriodReport.getAll,
      claims.documents.trialPeriodReport.create,
      claims.documents.trialPeriodReport.delete,
      claims.documents.trialPeriodReport.edit,
      claims.documents.trialPeriodReport.download,
      claims.documents.trialPeriodReport.upload,
    ],
  },

  {
    path: AuthRoutes.educationPlans,
    exact: true,
    name: 'education_plan',
    icon: 'fa-solid fa-map fa-lg',
    component: (
      <Suspense fallback={<LoadingSpinner />}>
        <EducationPlanModule />
      </Suspense>
    ),
    mainModule: claims.gradeModule.main,
    subModules: [],
  },
  {
    path: AuthRoutes.modules,
    exact: true,
    name: 'module',
    icon: 'fas fa-solid fa-puzzle-piece fa-lg',
    component: <ApprenticeshipModuleModule />,
    mainModule: claims.gradeModule.main,
    subModules: [
      claims.gradeModule.apprenticeshipModules.main,
      claims.gradeModule.apprenticeshipModules.getAll,
      claims.gradeModule.apprenticeshipModules.create,
      claims.gradeModule.apprenticeshipModules.edit,
      claims.gradeModule.apprenticeshipModules.delete,
      claims.gradeModule.apprenticeshipModules.getSingle,
    ],
  },
]

export const AppHeaderRoutes_NonAuthorized: MokaiRoute[] = [
  {
    path: PublicRoutes.about,
    exact: true,
    name: 'about',
    icon: 'fas fa-mountain fa-lg',
    component: <About />,
    mainModule: claims.basic.main,
  },
]

export const SubRoutes_Authorized: MokaiRoute[] = [
  {
    path: AuthRoutes.editPerson,
    exact: true,
    name: 'empty',
    icon: '',
    component: <EditPerson />,
    mainModule: claims.hrm.main, //'001-000-000',
    subModules: [
      claims.hrm.person.updateSinglePerson,
      claims.hrm.apprentices.update,
    ],
  },

  {
    path: AuthRoutes.addPerson,
    exact: true,
    name: 'new_person',
    icon: '',
    component: <EditPerson />,
    mainModule: claims.hrm.main, //'001-000-000',
    subModules: [claims.hrm.persons.create, claims.hrm.apprentices.create],
  },
  {
    path: AuthRoutes.groups,
    exact: true,
    name: 'group_management',
    icon: '',
    component: <GroupModule />,
    mainModule: claims.hrm.main,
    subModules: [claims.hrm.groups.main, claims.hrm.groups.getAll],
  },

  {
    path: AuthRoutes.editGroup,
    exact: true,
    name: 'empty',
    icon: '',
    component: <GroupEntry />,
    mainModule: claims.hrm.main,
    subModules: [
      claims.hrm.groups.main,
      claims.hrm.groups.getSingle,
      claims.hrm.groups.delete,
      claims.hrm.groups.update,
      claims.hrm.groups.createMember,
      claims.hrm.groups.updateMember,
      claims.hrm.groups.deleteMember,
    ],
  },

  {
    path: AuthRoutes.dossiers,
    exact: true,
    name: 'dossiers',
    icon: 'fa-solid fa-folder-open',
    component: (
      <Suspense fallback={<LoadingSpinner />}>
        <AllDossiersList />
      </Suspense>
    ),
    mainModule: claims.documents.dossier.main,
    subModules: [claims.documents.dossier.getAll],
  },
  {
    path: AuthRoutes.accounts,
    exact: true,
    name: 'accounts',
    icon: 'fa-solid fa-user-shield fa-lg',
    component: <AccountList />,
    mainModule: claims.hrm.main,
    subModules: [claims.hrm.account.main],
  },
  {
    path: AuthRoutes.editAccount,
    exact: true,
    name: 'edit_account',
    icon: 'fa-solid fa-user-shield fa-lg',
    component: <EditAccount />,
    mainModule: claims.hrm.main,
    subModules: [claims.hrm.person.updateSinglePerson],
  },
  {
    path: AuthRoutes.addEducationPlan,
    exact: true,
    name: 'add_education_plan',
    icon: '',
    component: <AddEducationPlan />,
    mainModule: claims.gradeModule.main,
    subModules: [],
  },
  {
    path: AuthRoutes.editEducationPlan,
    exact: true,
    name: 'edit_education_plan',
    icon: '',
    component: <UpdateEducationPlan />,
    mainModule: claims.gradeModule.main,
    subModules: [],
  },
  {
    path: AuthRoutes.editGrade,
    exact: true,
    name: 'edit_grade',
    icon: '',
    component: (
      <Suspense>
        <GradeModule />
      </Suspense>
    ),
    mainModule: claims.documents.main,
    subModules: [
      claims.gradeModule.main,
      claims.gradeModule.apprenticeshipModuleGrades.getAll,
      claims.gradeModule.apprenticeshipModuleGrades.edit,
      claims.gradeModule.apprenticeshipModuleGrades.create,
      claims.gradeModule.apprenticeshipModuleGrades.delete,
      claims.gradeModule.apprenticeshipModuleGrades.getSingle,
    ],
  },
  {
    path: AuthRoutes.assignGradeContainer,
    exact: true,
    name: 'grade_assignment',
    icon: '',
    component: <CreateApprenticeGradeContainers />,
    mainModule: claims.gradeModule.main,
    subModules: [],
  },
  {
    path: AuthRoutes.assignGradeContainerById,
    exact: true,
    name: 'grade_assignment',
    icon: '',
    component: <CreateApprenticeGradeContainers />,
    mainModule: claims.gradeModule.main,
    subModules: [],
  },
  {
    path: AuthRoutes.companyEntry,
    exact: true,
    name: 'edit_company',
    icon: 'fas fa-building fa-lg',
    component: <UpdateCompanyEntry />,
    mainModule: claims.companies.main, //'002-000-000',
    subModules: [
      claims.companies.all.main, //'002-001-000',
      claims.companies.all.get, //'002-001-001',
      claims.companies.all.create, //'002-002-000',
      claims.companies.single.main, //'002-002-001',
      claims.companies.single.get, //'002-002-002',
      claims.companies.single.update, //'002-002-003'
    ],
  },
  {
    path: AuthRoutes.addCompany,
    exact: true,
    name: 'add_company',
    icon: 'fas fa-building fa-lg',
    component: <AddCompanyEntry />,
    mainModule: claims.companies.main, //'002-000-000',
    subModules: [
      claims.companies.all.main, //'002-001-000',
      claims.companies.all.get, //'002-001-001',
      claims.companies.all.create, //'002-002-000',
      claims.companies.single.main, //'002-002-001',
    ],
  },
  {
    path: AuthRoutes.educationReportApprentice,
    exact: true,
    name: 'education_report',
    icon: 'fas fa-graduation-cap fa-lg',
    component: <EducationReportEntry />,
    mainModule: claims.documents.main, // '003-000-000',
    subModules: [
      claims.documents.educationReports.main, //'003-001-000'
    ],
  },

  {
    path: AuthRoutes.educationReportEntry,
    exact: true,
    name: 'education_report',
    icon: 'fas fa-graduation-cap fa-lg',
    component: <EducationReportEntry />,
    mainModule: claims.documents.main, // '003-000-000',
    subModules: [
      claims.documents.educationReports.main, //'003-001-000'
    ],
  },

  {
    path: AuthRoutes.trialPeriodReportEntry,
    exact: true,
    name: 'edit_trial_period_report',
    icon: 'fas fa-graduation-cap fa-lg',
    component: <TrialPeriodReportEdit />,
    mainModule: claims.documents.main,
    subModules: [
      claims.documents.trialPeriodReport.edit,
      claims.documents.trialPeriodReport.download,
      claims.documents.trialPeriodReport.upload,
    ],
  },

  {
    path: AuthRoutes.trialPeriodReportNew,
    exact: true,
    name: 'new_trial_period_report',
    icon: 'fas fa-graduation-cap fa-lg',
    component: <TrialPeriodReportEdit />,
    mainModule: claims.documents.main,
    subModules: [
      claims.documents.trialPeriodReport.edit,
      claims.documents.trialPeriodReport.download,
      claims.documents.trialPeriodReport.upload,
    ],
  },

  {
    path: AuthRoutes.addJournal,
    exact: true,
    icon: '',
    component: <JournalEntry/>,
    name: 'create_journal_entry',
    mainModule: claims.learningJournals.main,
    subModules: [
      claims.learningJournals.general.create,
    ]
  },

  {
    path: AuthRoutes.editJournal,
    exact: true,
    icon: '',
    component: <JournalEntry/>,
    name: 'edit_journal_entry',
    mainModule: claims.learningJournals.main,
    subModules: [
      claims.learningJournals.edit.update,
      claims.learningJournals.review.update
    ]
  },

  {
    path: AuthRoutes.listJournals,
    exact: true,
    icon: '',
    component: <JournalDetail/>,
    name: 'journal_entries',
    mainModule: claims.learningJournals.main,
    subModules: [
      claims.learningJournals.general.get,
      claims.learningJournals.general.getAll
    ]
  }
]

export const AppHeaderRoutes_Authorized: MokaiRoute[] = [
  {
    path: AuthRoutes.profile,
    exact: true,
    name: 'profile',
    icon: 'fas fa-user fa-lg',
    component: <Profile />,
    mainModule: claims.basic.main, // '000-000-000',
  },
  {
    path: AuthRoutes.company,
    exact: true,
    name: 'company',
    icon: 'fas fa-building fa-lg',
    component: <CompanyModule />,
    mainModule: claims.companies.main, //'002-000-000',
    subModules: [
      claims.companies.all.main, // '002-001-000',
      claims.companies.single.main, // '002-002-000'
    ],
  },
  // {
  //     path: AuthRoutes.Settings,
  //     exact: true,
  //     name: 'Settings',
  //     icon: 'fas fa-cogs fa-lg',
  //     component: Settings,
  //     requiredRoles: [Role.SuperAdmin, Role.Admin]
  // },
  {
    path: PublicRoutes.about,
    exact: true,
    name: 'about',
    icon: 'fas fa-mountain fa-lg',
    component: <About />,
    mainModule: claims.basic.main,
  },
]

export const AllRoutes: MokaiRoute[] = SidebarRoutes_Authorized
	// .concat(SidebarRoutes_Authorized_SubRoutes)
	.concat(AppHeaderRoutes_Authorized)
	.concat(SubRoutes_Authorized)
	.concat(AppHeaderRoutes_NonAuthorized)

export default SidebarRoutes_Authorized